import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import { EditOutlined, NoteAddOutlined } from "@mui/icons-material";
import { Product } from "../../../types";
import MarkdownContent from "../../common/MarkdownContent";
import { SavePatch } from "./useProductQuickSave";

interface Props {
    product: Product;
    editable: boolean;
    save: SavePatch;
    onDirtyChange: (dirty: boolean) => void;
}

/**
 * The product's description in the drawer: shown as on the website, with
 * Add / Edit description for a quick change in place (Markdown, like the
 * full form).
 */
export const DescriptionEditor: React.FC<Props> = ({ product, editable, save, onDirtyChange }) => {
    const current = product.description ?? "";
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(current);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
        setEditing(false);
        setError("");
    }, [product.product_id, current]);

    const dirty = editing && draft.trim() !== current.trim();
    useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

    useEffect(() => {
        const el = inputRef.current;
        if (editing && el) {
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        }
    }, [editing]);

    const start = () => {
        setDraft(current);
        setError("");
        setEditing(true);
    };
    const cancel = () => {
        setEditing(false);
        setError("");
    };
    const submit = async () => {
        if (!dirty || busy) return;
        setBusy(true);
        const text = draft.trim();
        const failed = await save({ description: text }, text ? "Description saved" : "Description removed");
        setBusy(false);
        if (failed) setError(failed);
    };

    if (!current && !editing) {
        return editable ? (
            <Box sx={{ mb: 3 }}>
                <Button size="small" startIcon={<NoteAddOutlined fontSize="small" />} onClick={start}>
                    Add description
                </Button>
            </Box>
        ) : null;
    }

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    DESCRIPTION
                </Typography>
                {editable && !editing && (
                    <Button size="small" startIcon={<EditOutlined fontSize="small" />} onClick={start} sx={{ my: -0.5 }}>
                        Edit description
                    </Button>
                )}
            </Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                {editing ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <TextField
                            inputRef={inputRef}
                            value={draft}
                            onChange={(e) => {
                                setDraft(e.target.value);
                                setError("");
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                    e.preventDefault();
                                    void submit();
                                } else if (e.key === "Escape") {
                                    e.stopPropagation();
                                    cancel();
                                }
                            }}
                            multiline
                            minRows={4}
                            maxRows={14}
                            fullWidth
                            size="small"
                            placeholder="What it is, who it's for, ingredients, how to use…"
                            helperText="Shown on the product's page. **Bold**, - lists and line breaks work."
                            disabled={busy}
                            inputProps={{ "aria-label": "Description" }}
                        />
                        {error && (
                            <Typography variant="caption" color="error">
                                {error}
                            </Typography>
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mr: "auto", display: { xs: "none", sm: "block" } }}
                            >
                                Ctrl/⌘+Enter to save · Esc to cancel
                            </Typography>
                            <Button size="small" color="inherit" onClick={cancel} disabled={busy}>
                                Cancel
                            </Button>
                            <Button size="small" variant="contained" onClick={() => void submit()} disabled={!dirty || busy}>
                                {busy ? "Saving…" : "Save description"}
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <MarkdownContent content={current} />
                )}
            </Paper>
        </>
    );
};

export default DescriptionEditor;
